<template>
  <resize-observer :breakpoints="breakpoints">
    <sb-panel slot-scope="{ el }" class="pricing-details" :class="el.is">
      <div class="pricing-header">
        <div class="sb-nav-link-section">
          <a id="dashboard-link" :href="dashboardRoute" class="sb-nav-link"><span class="icon-chevron-left font-size-22" />Dashboard</a>
        </div>
        <br>
        <h2>Pricing</h2>
        <p>
          Pricing at ShipBob is composed of Fulfillment Cost, Storage, Receiving and Additional Services.
          <span class="hide-large-screen" />
          <span class="bold link" @click="togglePricingDetails">
            {{ pricingDetailToggleText }}
            <img v-if="isPricingDetailVisible" class="arrow-icon" src="@assets/images/arrow-up.svg">
            <img v-else class="arrow-icon" src="@assets/images/arrow-down.svg">
          </span>
        </p>
        <sb-accordion :is-visible="isPricingDetailVisible">
          <pricing-breakdown-with-currency />
        </sb-accordion>
        <transition name="accordion-action">
          <div v-if="isPricingDetailVisible" class="action">
            <span class="link" @click="togglePricingDetails">
              Hide <img class="arrow-up-icon" src="@assets/images/arrow-up.svg">
            </span>
          </div>
        </transition>
      </div>
      <resize-observer :breakpoints="breakpoints">
        <div
          slot-scope="{ el }"
          class="panelContentHeadsUpWrapper"
          :class="el.is"
        >
          <div class="panel-content padding-bottom-0">
            <span class="panel-content-heading">Fulfillment Cost Estimate</span>
            <p class="helper-text">
              Make selections below to get your fulfillment cost estimate.
              Please note, this pricing factors in picking, packing, standard packaging, and shipping costs.
            </p>
          </div>
        </div>
      </resize-observer>
      <resize-observer :breakpoints="formBreakpoints">
        <div slot-scope="{ el }" class="panel-content-form-wrapper" :class="el.is">
          <div class="form-content">
            <pricing-form />
          </div>
          <div class="form-action">
            <sb-button id="pricing-btn" :disabled="inputValsLoading || !selectionSatisfied" variation="primary" @click="updatePricing">
              Estimate Cost
            </sb-button>
          </div>
        </div>
      </resize-observer>
      <resize-observer :breakpoints="breakpoints">
        <div slot-scope="{ el }" class="panel-weight-info" :class="el.is">
          <template v-if="!firstSearch">
            <div class="panel-content">
              <span class="panel-content-heading">Billable Weight</span>
              <p class="helper-text">
                ShipBob uses Billable Weight to calculate your fulfillment rates.
                Use our
                <span class="inline-icon-text link" @click="setCalculatorVisibility(true)">
                  <img src="../assets/images/calculator.svg">
                  Billable Weight Estimator
                </span>
                to estimate your billable weight.
                Please note,
                <template v-if="enableDimDivisorByCountry">
                  that
                </template> if you choose to estimate your billable weight without our Estimator tool, make sure to include dunnage and packaging in your estimate.
              </p>
              <p v-if="enableDimDivisorByCountry && !shouldUseActualWeightForPricingEstimation">
                <span class="billable-weight-text">Billable weight can be determined as follows,
                  <help-center-link link-key="pricing/billableWeightEstimator">learn more</help-center-link>:
                </span>
                <ul>
                  <li><strong>U.S. orders under 16oz:</strong> Based on actual weight</li>
                  <li>
                    <strong>U.S. orders 16oz or greater:</strong> Based on the larger of
                    <help-center-link link-key="pricing/dimensionalWeight">
                      dimensional weight
                    </help-center-link>
                    and actual weight
                  </li>
                  <li>
                    <strong>CA, UK, EU, and AUS orders:</strong> Based on the larger of
                    <help-center-link link-key="pricing/dimensionalWeight">
                      dimensional weight
                    </help-center-link>
                    and actual weight
                  </li>
                </ul>
              </p>
              <p v-else-if="enableDimDivisorByCountry && shouldUseActualWeightForPricingEstimation">
                <span class="billable-weight-text">Billable weight can be determined as follows,
                  <help-center-link link-key="pricing/billableWeightEstimator">learn more</help-center-link>:
                </span>
                <ul>
                  <li><strong>U.S. orders under 16oz:</strong> Based on actual weight</li>
                  <li>
                    <strong>U.S. orders 16oz or greater:</strong> Based on the larger of
                    <help-center-link link-key="pricing/dimensionalWeight">
                      dimensional weight
                    </help-center-link>
                    and actual weight
                  </li>
                  <li>
                    <strong>CA, AUS orders:</strong> Based on the larger of
                    <help-center-link link-key="pricing/dimensionalWeight">
                      dimensional weight
                    </help-center-link>
                    and actual weight
                  </li>
                  <li><strong>UK, EU orders:</strong> Based on actual weight</li>
                </ul>
              </p>
              <p v-else>
                <span class="billable-weight-text">Billable weight can be determined as follows:</span>
                <ul>
                  <li><strong>U.S. Orders under 16oz:</strong> Based on actual weight</li>
                  <li>
                    <strong>U.S. Orders 16oz or greater:</strong> Based on the larger of
                    <help-center-link link-key="pricing/dimensionalWeight">
                      dimensional weight
                    </help-center-link>
                    and actual weight
                  </li>
                  <li><strong>Non-U.S. Orders:</strong> Based on dimensional weight</li>
                </ul>
              </p>
            </div>
            <heads-up-panel title="Heads up!">
              <p v-if="enableDimDivisorByCountry" class="text-normal last">
                If the actual weight of your order shipping from the US falls between 15.1 - 15.99oz, we will round it up to 16oz. At this point, your billable weight will be higher of dimensional weight and your actual weight.
              </p>
              <p v-else class="text-normal last">
                If the actual weight of your order falls between 15.1-15.99oz, we will round up to 16oz.
                At this point, your billable weight will be the larger of dimensional weight and actual weight.
              </p>
            </heads-up-panel>
          </template>
        </div>
      </resize-observer>
      <div class="pricing-table">
        <expand-transition>
          <row v-if="!inputValsLoading" class="info">
            <column size="12" breakpoint="md">
              <pricing-table @calc-icon-click="setCalculatorVisibility(true)" />
            </column>
          </row>
        </expand-transition>
      </div>
      <weight-calculator-modal
        v-if="showCalculator"
        @close-modal="closeModal()"
      />
    </sb-panel>
  </resize-observer>
</template>

<script>
import HeadsUpPanel from './HeadsUp'
import PricingTable from '../PricingTable'
import PricingForm from './forms/PricingForm'
import PricingBreakdownWithCurrency from './breakdown/PricingBreakdownWithCurrency'
import WeightCalculatorModal from './calculator/WeightCalculatorModal'
import SbAccordion from '@/components/utils/SbAccordion'
export default {
  name: 'PricingDetails',
  components: {
    HeadsUpPanel,
    PricingTable,
    PricingForm,
    SbAccordion,
    PricingBreakdownWithCurrency,
    WeightCalculatorModal
  },
  data: () => ({
    breakpoints: {
      wrap: el => el.width < 750,
      full: el => el.width >= 750
    },
    formBreakpoints: {
      sm: el => el.width < 480,
      md: el => el.width >= 480 && el.width < 720,
      lg: el => el.width >= 720 && el.width < 1024,
      xl: el => el.width >= 1024
    },
    inputValsLoading: true,
    searchedDestinationCountries: '',
    shippingOptionId: -1,
    firstSearch: true,
    isPricingDetailVisible: false,
    showCalculator: false
  }),
  computed: {
    selectedDestinationCountries () {
      return this.$shipBobStore.getters['pricing/selectedDestinationCountries']
    },
    showNetworkOption () {
      return this.$shipBobStore.getters['pricing/showNetworkOption']
    },
    selectionSatisfied () {
      const selectedDestCountries = this.getSelectedDestCountryString()
      /*
        If the list of destination countries has changed - allow a new search
        If it is anything after the first search and the user changes weight or shipping
          allow a new search
      */
      return (!!selectedDestCountries &&
              this.selectedDestinationCountries.length > 0 &&
              this.hasOriginCountryChanged &&
              (!this.showNetworkOption || this.hasNetworkChanged) &&
              this.searchedDestinationCountries !== selectedDestCountries) ||
              (!this.firstSearch &&
              (this.unitOfMeasure.Abbreviation !== this.unitOfMeasureForm.Abbreviation ||
              this.selectedShippingOption.id !== this.shippingOptionId ||
              ((this.hasOriginCountryChanged || (!this.showNetworkOption || this.hasNetworkChanged) ||
              this.searchedDestinationCountries !== selectedDestCountries) &&
              this.selectedDestinationCountries.length > 0)
              ))
    },
    loadingInputs () {
      return this.$shipBobStore.getters['pricing/loading']('inputs')
    },
    formattedDimensionalWeightCutOff () {
      return `${this.dimensionalWeightCutOff} ${this.unitOfMeasure.Abbreviation}`
    },
    dimensionalWeightCutOff () {
      return this.$shipBobStore.getters['pricing/dimensionalWeightCutOff']
    },
    unitOfMeasure () {
      return this.$shipBobStore.getters['pricing/unitOfMeasure']
    },
    unitOfMeasureForm () {
      return this.$shipBobStore.getters['pricing/unitOfMeasureForm']
    },
    dashboardRoute () {
      return this.$sbAppSettings.photoPrint('/#/')
    },
    selectedShippingOption () {
      return this.$shipBobStore.getters['pricing/selectedShippingOption']
    },
    pricingDetailToggleText () {
      const toggleText = this.isPricingDetailVisible ? 'Hide' : 'Show'
      return `${toggleText} Pricing Breakdown`
    },
    hasOriginCountryChanged () {
      return this.$shipBobStore.getters['pricing/hasOriginCountryChanged']
    },
    hasNetworkChanged () {
      return this.$shipBobStore.getters['pricing/hasNetworkChanged']
    },
    isPricingDataAvailable () {
      return this.$shipBobStore.getters['pricing/isDataAvailable']
    },
    shouldUseActualWeightForPricingEstimation () {
      return this.$shipBobStore.getters.useActualWeightForPricingEstimation
    },
    enableDimDivisorByCountry () {
      return this.$shipBobStore.getters.enableDimDivisorByCountry
    }
  },
  watch: {
    loadingInputs () {
      const loading = this.loadingInputs
      if (loading && loading.then) {
        loading.then(_val => {
          this.inputValsLoading = false
        })
      }
    },
    // hides outer scrollbar when modal is visible
    showCalculator: function () {
      if (this.showCalculator) {
        document.querySelector('body').style.overflow = 'hidden'
        return
      }

      document.querySelector('body').style.overflow = 'auto'
    }
  },
  methods: {
    async updatePricing () {
      this.searchedDestinationCountries = this.getSelectedDestCountryString()
      this.firstSearch = false
      this.shippingOptionId = this.selectedShippingOption.id
      await this.$shipBobStore.dispatch('pricing/setPricingUpdated')
      const promise = this.$shipBobStore.dispatch('pricing/getPricingPlan')
      this.$shipBobStore.dispatch('pricing/addLoading', { promise, scopes: ['table'] })
    },
    getSelectedDestCountryString () {
      return this.selectedDestinationCountries
        .map(x => x.name)
        .sort((a, b) => a > b ? 1 : -1)
        .join(',')
    },
    togglePricingDetails () {
      this.isPricingDetailVisible = !this.isPricingDetailVisible
    },
    setCalculatorVisibility (visibility) {
      this.showCalculator = visibility
    },
    closeModal () {
      this.setCalculatorVisibility(false)
    }
  }
}
</script>

<style lang='scss'>
@import './pricing-details-panel';
@import '../../../_colors';
@import 'node_modules/shipbob-design-system/dist/system/tokens/tokens.scss';

$linkColor: #2470AF;

.pricing-details {
  display: block !important;
  width: 100%;
  padding: 0;
  padding: 0;

  &.sb-panel-default {
    padding: 0 !important;
  }

  &.full {
    .pricing-header {
      padding: 2em 3em 1.4em 3em;

      .hide-large-screen {
        display: none;
      }
    }

    .panel-content {
      padding: 2em 3em;
    }

    .sub-panel-wrapper {
      padding: 2rem 3rem;
    }

    .panel-content-form-wrapper {
      margin: 0 3em 2em 3em;
    }
  }

  &.wrap {
    .pricing-header {
      padding: 1em 1em 1.4em 1em;

      .hide-large-screen {
        display: block;
      }
    }

    .panel-content {
      padding: 2em 1em;
    }

    .sub-panel-wrapper {
      padding: 2rem 1rem;
    }

    .panel-content-form-wrapper {
      margin: 0 1em 2em 1em;
    }
  }

  .panelContentHeadsUpWrapper {
    display: flex;
    justify-content: space-between;
  }

  .panel-weight-info {
    display: flex;
    justify-content: space-between;

    .panel-content {
      padding-top: 0;
      max-width: 60em;

      .helper-text {
        margin-bottom: 0.4em !important;
      }

      .billable-weight-text {
        line-height: 2.4em;
      }
    }
  }

  .sb-nav-link-section {
    margin-bottom: 4px;
  }

  .bold {
    font-weight: bold
  }

  .pricing-header {
    background:$light-grey-background;
    padding: 2em 2em 1.4em 3em;

    h2 {
      color :$grey;
    }

    p {
      color:$medium-grey;
    }

    .sb-nav-link {
      font-size: .875rem;
      font-weight: 600;
      text-decoration: none;
      color:$color-primary;
      display: flex;
      align-items: center;
    }

    .icon-chevron-left:before {
      content: "\E909";
    }
  }

  .unset-position {
    position: unset;
  }

  .panel-content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    h4 {
      margin: 0;
      color : $grey;
    }

    .helper-text {
      margin-bottom: 2em;
    }

    .info {
      margin-bottom: 1em;

      &.last {
        margin: 0;
      }
    }

    .left {
      .control {
        margin-right: 0.5em;
      }
    }

    .right {
      &.sb-flex-wrapped {
        .control { margin-left: 0; }
      }
      .control {
        margin-left: 0.5em;
      }
    }

    .attributes-label {
      color: $blue-grey;
      font-size: .875rem;
      font-weight: 600;
    }

    .control {
      display: flex;
      align-items: center;
      margin-bottom: 1em;

      &.no-space-bottom {
        margin-bottom: 0;
      }

      .tooltip-activator {
        text-decoration: none;
        color: $color-primary;
      }

      p {
        margin: 0;
      }

      .tooltip-content {
        font-weight: 700;
        padding: 2em;
      }
    }

    .dropdown-control {
      max-width: 13.8em;
      margin: 0 2rem 1em;
    }

    .inline-icon-text {
      display: inline-flex;
      align-content: center;
      vertical-align: bottom;

      img {
        margin-right: 4px;
      }
    }
  }

  .pricing-table {
    max-width: 100%;
    flex: 1;
    padding: 0;

    .price-by-zone {
      padding: 2rem;
    }

    .threshold-text {
      text-align: center;
      background-color: #DDE0E2;
      color: #5C6E8D;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0;
    }
   }

  .action {
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 600;
    margin-top: 2em;
  }

  .arrow-icon {
    margin-left: 0.2em;
    height: 12px;
    width: 11px;
  }

  .link {
    color: $linkColor;
    cursor: pointer;

    &:hover {
      color: darken($linkColor, 5);
    }
  }

  .accordion-action-enter,
  .accordion-action-leave-to {
    max-height: 0;
  }

  .accordion-action-enter-active,
  .accordion-action-leave-active {
    transition: height, 0.5s linear;
  }

  .accordion-action-enter-to,
  .accordion-action-leave {
    max-height: 100px;
  }

  .text-normal {
    font-size: 14px;
    font-weight: 400;
    line-height: 17px;
  }

  .panel-content-heading {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 10px;
  }

  .panel-content-form-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;

    &.xl {
      .form-content {
        width: 85%;
      }

      .form-action {
        width: 15%;
        justify-content: flex-start;
      }
    }

    &.lg {
      .form-content {
        width: 75%;
      }

      .form-action {
        width: 25%;
        justify-content: flex-end;
        margin-top: 1em;
      }
    }

    &.sm, &.md {
      .form-content {
        width: 100%;
      }

      .form-action {
        width: 100%;
        justify-content: center;
        margin-bottom: 1em;
      }
    }
  }

  .form-content {
    display: flex;
    margin-bottom: 1.75em;
  }

  .form-action {
    display: flex;

    #pricing-btn {
      padding-left: 3em;
      padding-right: 3em;
    }
  }

  #dashboard-link {
    margin-left: -0.5em;
  }

  .padding-bottom-0 {
    padding-bottom: 0 !important;
  }

  #table-calculator-icon {
    padding-left: 7.5px;
    cursor: pointer;
  }

  .accordion {
    margin-right: 10em;

    .accordion-content {
      padding-top: 2em;
    }
  }
}
</style>
